import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { obtenerIniciales } from "@/lib/iniciales";

import type { FilaDelCuadro, ModoDelTerreno } from "./cuadroDeConstrucciones";
import { momentoDeSincronizacion, pluralizar } from "./registroDeTerreno";
import { TarjetaDeRegistroDeTerreno } from "./TarjetaDeRegistroDeTerreno";

interface RegistroDeTerrenoDeLaConstruccionProps {
  fila: FilaDelCuadro;
  modo: Exclude<ModoDelTerreno, "oculto">;
  informeId: number;
  encargado: string | null;
  /** Abre la foto en el visor de la pestaña. */
  onAbrirFoto: (uuid: string) => void;
}

/** La línea de arriba del panel: cuánto trajo la visita para ESTA construcción. */
function metaDelPanel(fila: FilaDelCuadro, modo: Exclude<ModoDelTerreno, "oculto">): string {
  if (modo === "pendiente") return "Visita pendiente";

  const registros = fila.item?.registros ?? [];
  const notas = fila.notas.length;
  const cuantos = registros.length > 0 ? pluralizar(registros.length, "registro", "registros") : "sin registros";

  if (notas > 0) return `${pluralizar(notas, "nota en datos", "notas en datos")} · ${cuantos}`;
  if (registros.length === 0) return "Sin registros";

  const momentos = registros
    .map((r) => r.sincronizado_en)
    .filter((m): m is string => m !== null)
    .sort();
  const ultima = momentos[momentos.length - 1];

  return ultima ? `${cuantos} · Sincronizado ${momentoDeSincronizacion(ultima)}` : cuantos;
}

/**
 * El panel derecho del modal: lo que el encargado registró en terreno sobre
 * ESTA construcción —fotos, notas de voz con su transcripción y textos—.
 *
 * Es de LECTURA: no copia nada al formulario. Lo que sí se ofrece es ampliar
 * una foto en el visor de la pestaña.
 */
export function RegistroDeTerrenoDeLaConstruccion({
  fila,
  modo,
  informeId,
  encargado,
  onAbrirFoto,
}: RegistroDeTerrenoDeLaConstruccionProps) {
  const registros = fila.item?.registros ?? [];
  const quien = encargado ?? "El encargado";

  return (
    <aside
      aria-label="Registro de terreno"
      className="bg-superficie-sutil border-divisor flex min-h-0 flex-col border-t-[1.5px] lg:border-t-0 lg:border-l-[1.5px]"
    >
      <div className="bg-card border-divisor-suave flex flex-none items-center gap-2.5 border-b px-3.5 py-3">
        <Avatar className="size-6">
          <AvatarFallback className="bg-primary text-primary-foreground text-[0.59375rem] font-extrabold">
            {encargado ? obtenerIniciales(encargado) : "?"}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="text-ink text-xs font-bold">Registro de terreno</div>
          <div className="text-ink-muted text-[0.65625rem] font-semibold">{metaDelPanel(fila, modo)}</div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto p-2.5">
        {modo === "pendiente" ? (
          <p className="text-ink-muted px-3 py-5.5 text-center text-xs font-semibold text-pretty">
            {quien} aún no sincroniza la visita.
          </p>
        ) : registros.length === 0 ? (
          <p className="text-ink-muted px-3 py-5.5 text-center text-xs font-semibold text-pretty">
            Esta construcción no tiene registros vinculados en terreno.
          </p>
        ) : (
          registros.map((r, indice) => (
            <TarjetaDeRegistroDeTerreno
              key={r.uuid}
              informeId={informeId}
              registro={r}
              indice={indice}
              de={fila.tipo}
              onAbrirFoto={onAbrirFoto}
            />
          ))
        )}
      </div>
    </aside>
  );
}
